import Head from 'next/head';
import { useRouter } from 'next/router';

const SITE_URL = 'https://www.dataabyss.ai';
const DEFAULT_OG_IMAGE = `${SITE_URL}/favicons/dark/android-chrome-512x512.png`;

export interface SEOProps {
  title?: string;
  description?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile';
  noIndex?: boolean;
  jsonLd?: Record<string, unknown>;
}

/**
 * SEO Component for setting page-specific meta tags
 *
 * @param title - Page title (will be appended with " | Data Abyss")
 * @param description - Page description for meta tags
 * @param ogImage - Open Graph image URL
 * @param ogType - Open Graph type (website, article, profile)
 * @param noIndex - If true, tells search engines not to index this page
 * @param jsonLd - JSON-LD structured data object
 */
export function SEO({
  title,
  description,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  noIndex = false,
  jsonLd,
}: SEOProps) {
  const router = useRouter();
  const canonicalUrl = `${SITE_URL}${router.asPath.split('?')[0]}`;

  const pageTitle = title
    ? `${title} | Data Abyss`
    : 'Data Abyss - Intelligence Analysis Platform';

  const pageDescription =
    description ||
    'Data Abyss offers platforms and solutions that empower users to search and identify valuable intelligence using advanced algorithms and visual analytics.';

  return (
    <Head>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Data Abyss" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
    </Head>
  );
}

/**
 * Generate Organization schema for JSON-LD
 * @param includeContext - Whether to include @context (set false when used in @graph)
 */
export function getOrganizationSchema(includeContext = false) {
  const schema: Record<string, unknown> = {
    '@type': 'Organization',
    name: 'Data Abyss',
    url: SITE_URL,
    logo: `${SITE_URL}/favicons/dark/android-chrome-512x512.png`,
    description:
      'Intelligence analysis platform empowering users to search and identify valuable intelligence using advanced algorithms and visual analytics.',
    sameAs: [],
  };
  if (includeContext) {
    schema['@context'] = 'https://schema.org';
  }
  return schema;
}

/**
 * Generate WebSite schema for JSON-LD
 * @param includeContext - Whether to include @context (set false when used in @graph)
 */
export function getWebSiteSchema(includeContext = false) {
  const schema: Record<string, unknown> = {
    '@type': 'WebSite',
    name: 'Data Abyss',
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/search/unified?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
  if (includeContext) {
    schema['@context'] = 'https://schema.org';
  }
  return schema;
}

/**
 * Generate Article schema for JSON-LD
 * @param includeContext - Whether to include @context (set false when used in @graph)
 */
export function getArticleSchema(
  params: {
    title: string;
    description: string;
    datePublished?: string;
    dateModified?: string;
    author?: string;
    url: string;
  },
  includeContext = false,
) {
  const schema: Record<string, unknown> = {
    '@type': 'Article',
    headline: params.title,
    description: params.description,
    datePublished: params.datePublished,
    dateModified: params.dateModified || params.datePublished,
    author: {
      '@type': 'Organization',
      name: params.author || 'Data Abyss',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Data Abyss',
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/favicons/dark/android-chrome-512x512.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': params.url,
    },
  };
  if (includeContext) {
    schema['@context'] = 'https://schema.org';
  }
  return schema;
}

/**
 * Generate BreadcrumbList schema for JSON-LD
 */
export function getBreadcrumbSchema(
  breadcrumbs: Array<{ name: string; url: string }>,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  };
}
